import type { RouteLocationNormalized } from 'vue-router'

export function getMetaFromRoute(nextRoute: RouteLocationNormalized) {
  let routeMeta =
    nextRoute.meta && Array.isArray(nextRoute.meta)
      ? (nextRoute.meta || [{}]).reduce(
          (acc: any, obj: any) => Object.assign(acc, obj),
          {},
        )
      : nextRoute.meta || {}
  // for some reason meta is not always on the component
  // but can be on the matched component default options
  // so let's merge that in
  const matchedMeta = nextRoute.matched?.length
    ? nextRoute.matched[0].components?.default?.options?.meta
    : {}
  routeMeta = { ...routeMeta, ...matchedMeta }
  return routeMeta
}

/**
 * Generates a URL with the specified locale prefix
 * @param href The URL or path to localize
 * @param locale The target locale to use for the prefix
 * @returns URL with the locale prefix
 */
function generateLocalisedUrl(href: string, locale: string): string {
  const urlWithNoLeadingSlash = href.replace(/^\/+/g, '')
  return `/${locale}/${urlWithNoLeadingSlash}`
}

/**
 * Determines if and how to apply locale prefix to a URL
 * @param params Object containing URL and locale information
 * @returns URL with appropriate locale handling
 */
function getUrl({
  currentUrl,
  targetLocale,
  defaultLocale,
}: {
  currentUrl: string
  targetLocale: string
  defaultLocale: string
}): string {
  // If URL is not a path (has protocol), return as is
  if (currentUrl.includes('://')) {
    return currentUrl
  }

  const targetLocalePart =
    targetLocale === defaultLocale ? undefined : `/${targetLocale}/`

  if (
    currentUrl.startsWith('/') &&
    targetLocalePart &&
    !currentUrl.startsWith(targetLocalePart)
  ) {
    return generateLocalisedUrl(currentUrl, targetLocale)
  }

  return currentUrl
}

/**
 * Ensures a URL has the correct locale prefix
 * @param route The URL string or route object to localize
 * @param locale The target locale to use
 * @param defaultLocale The default locale (which doesn't need prefix)
 * @param router Optional router instance (required when passing a route object)
 * @returns The URL with the appropriate locale prefix
 */
export function localePath(
  route: string | Record<string, any>,
  locale: string,
  router?: any,
  defaultLocale: string = 'en-gb',
): string {
  // If route is a string, use it directly
  if (typeof route === 'string') {
    return getUrl({
      currentUrl: route,
      targetLocale: locale,
      defaultLocale,
    })
  }

  // If route is an object but no router is provided, throw an error
  if (!router) {
    console.warn('Router is required when passing a route object to localePath')
    return ''
  }

  try {
    // Resolve the route object to a URL using the router
    const currentUrl = router.resolve(route).href

    return getUrl({
      currentUrl,
      targetLocale: locale,
      defaultLocale,
    })
  } catch (error) {
    console.error('Error resolving route in localePath:', error)
    return ''
  }
}
